import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MenuOption, Table, TableColumn, TableMenu } from "../../components/Table";
import InventoryHeader from "./InventoryHeader";
import InventoryStats from "./InventoryStats";
import CategoryFilter from "./CategoryFilter";
import AddItemModal from "../../Modal/orders/AddItemModal";
import { initAllStores } from "../../utils/cache";
import { CategoryType } from "../../types/category";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { capitalizeFirst } from "../../utils/string";
import FullPageSpinner from "../../components/FullPageSpinner";
import { errorMessage, sendRequest } from "../../utils/api";
import { isEmptyObject, isOnline } from "../../utils/helper-function";
import { v4 as uuidv4 } from "uuid";
import type Game from "../../models/Game";
import type Laundry from "../../models/Laundry";
import { MappedGameLaundry } from "../../types/service";
import { PAGINATION_PAGE_NUMBER } from "../../constants";
import { syncServices } from "../../utils/sync";

const columns: TableColumn[] = [
  { key: "name", label: "Name" },
  { key: "category", label: "Category" },
  { key: "price", label: "Price (₦)" },
  {
    key: "status",
    label: "Status",
    getClassName: (value: string) =>
      value === "Unavailable" ? "text-red-600" : "text-green-600",
    responsive: "md"
  },
];

const title = {
  games: "Game",
  laundries: "Laundry",
};

const initFormData = {
  id: "",
  name: "",
  category: "",
  newCategory: "",
  price: "",
  // stock: undefined,
  status: "Available",
};

const GameLaundryInventory = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { type } = useParams<{ type: "games" | "laundries" }>();
  const categoryKey = type === "games" ? "gameCategory" : "laundryCategory";
  const { openModal, closeModal } = useModal();

  const [itemCategories, setItemCategories] = useState<CategoryType[]>([]);
  const [items, setItems] = useState<MappedGameLaundry[]>([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [autoFillData, setAutoFillData] = useState(initFormData);

  // Validate param
  useEffect(() => {
    const validActions = ["games", "laundries"];
    const safeAction = (type || "").toLowerCase().trim();
    if (!validActions.includes(safeAction)) {
      navigate("/404");
      return;
    }
  }, [type, navigate]);

  // Load Data
  useEffect(() => {
    const loadItems = async () => {
      const cache = await initAllStores();
     
         setLoading(true);

            const getItems = async (): Promise<(Game | Laundry)[]> => {
              return await cache.getAllBy(
                type as "games" | "laundries", {}, "name"
              );
            }

              let itemsData = await getItems();
            
                  if (!isEmptyObject(itemsData) && type) {
                    await syncServices([type]);
                    itemsData = await getItems();
                  }
      
      const mappedItems: MappedGameLaundry[] = itemsData.map((item) => {

        let categoryName = "";
        let categoryId = "";

        if (type === "games" && "gameCategory" in item) {
          categoryName = item.gameCategory.name;
          categoryId = item.gameCategory.id;
        } else if (type === "laundries" && "laundryCategory" in item) {
          categoryName = item.laundryCategory.name;
          categoryId = item.laundryCategory.id;
        }

        return {
          id: item.id,
          name: item.name,
          category: capitalizeFirst(categoryName ?? "Uncategorized"),
          categoryId: categoryId ?? `cat-${Date.now()}`,
          price: item.cashPrice.toLocaleString(),
          status: item.isAvailable ? "Available" : "Unavailable",
        };
      });

      const categories: CategoryType[] = [
        { id: "all", name: "All" },
        ...Array.from(
          itemsData.reduce((acc, item) => {
            if ("gameCategory" in item && item.gameCategory) {
              acc.set(item.gameCategory.id, item.gameCategory);
            } else if ("laundryCategory" in item && item.laundryCategory) {
              acc.set(item.laundryCategory.id, item.laundryCategory);
            }
            return acc;
          }, new Map<string, CategoryType>()).values()
        ),
      ];

      setItems(mappedItems);
      setItemCategories(categories);
       setLoading(false);
    };

    loadItems();
  }, [type]);

  // Filter
  const filteredItems = items.filter(
    (f) =>
      (activeCategory === "all" || f.categoryId === activeCategory) &&
      f.name.toLowerCase().includes(search.toLowerCase())
  );

  const showConfirmationModal = (data: any) => {

    const newItem = {
      item: {
        updatedAt: new Date(),
        createdAt: new Date(),
        id: data?.item?.id,
        name: capitalizeFirst(data?.item?.name),
        price: parseFloat(data?.item?.price),
        isAvailable: data?.item?.status === "Available",
      },
      category: {
        ...data.category,
        name: capitalizeFirst(data?.category?.name ?? ""),
        updatedAt: new Date(),
        createdAt: new Date(),
      },
    };

    const missingFields: string[] = [];
    if (!newItem?.item?.name) missingFields.push(`${title[type!]} Name`);
    if (!newItem?.item?.price) missingFields.push(`${title[type!]} Price`);
    if (!newItem?.category?.name) missingFields.push(`${title[type!]} Category`);

    if (missingFields.length > 0) {
      return openModal(
        "Missing Fields",
        `Please fill in: ${missingFields.join(", ")}`,
        closeModal,
        false,
        "Close"
      );
    }

    if (newItem.category.isNew && !isEdit) {
      const isExist = itemCategories.find(
        (cat) => cat.name.toLocaleLowerCase() === newItem.category.name.toLocaleLowerCase()
      );
      if (isExist)
        return openModal(
          "Duplicate Category Detected",
          `A category named "${capitalizeFirst(newItem.category.name)}" already exists. 
           Please select it from the list instead of creating a new one.`,
          closeModal,
          false,
          "Close"
        );
    }

    const isExist = items.find(
      (item) =>
        item.name.toLowerCase() === newItem.item.name.toLowerCase() &&
        item.id !== newItem.item.id
    );

    if (isExist) {
      return openModal(
        `Duplicate ${title[type!]} Detected`,
        `A ${title[type!].toLowerCase()} named "${newItem.item.name}" already exists.`,
        closeModal,
        false,
        "Close"
      );
    }

    openModal(
      isEdit ? "Confirm Update" : `Add New ${title[type!]}`,
      isEdit
        ? "Are you sure you want to update this item? The changes will overwrite the existing details."
        : `You're about to add "${newItem.item.name}" to "${newItem.category.name}" category.`,
      () => {
        if (isEdit) updateItem(newItem);
        else createItem(newItem);
      }
    );
  };

  const createItem = async (itemData: any) => {
    const cache = await initAllStores();
    setIsModalOpen(false);
    setLoading(true);
    try {
      if (!isOnline()) {
        const err = errorMessage(408);
        if (err) openModal(err.title, err.msg, closeModal, false, "Close");
        return;
      }

      itemData.item.id = uuidv4();

      if (isOnline()) {
        await sendRequest("POST", `/services/${title[type!].toLocaleLowerCase()}`, itemData);
      }

      setItems((prev) => [
        ...prev,
        {
          id: itemData.item.id,
          name: itemData.item.name,
          category: itemData.category.name,
          categoryId: itemData.category.id,
          price: itemData.item.price.toLocaleString(),
          status: itemData.item.isAvailable ? "Available" : "Unavailable",
        },
      ]);

      setItemCategories((prev) => {
        const exists = prev.some((cat) => cat.id === itemData.category.id);
        if (!exists && itemData.category.name.toLowerCase() !== "all") {
          return [...prev, {
            id: itemData.category.id,
            name: capitalizeFirst(itemData.category.name)
          }];
        }
        return prev;
      });

      await cache.saveOne(type!, {
        ...itemData.item,
        [`${categoryKey}Id`]: itemData.category.id,
        cashPrice: itemData.item.price,
        [categoryKey]: { ...itemData.category },
      });

      showToast("Item Added Successfully", { type: "success" });
    } catch (error: any) {
      console.log(error);
      const err = errorMessage(error.status);
      if (err) openModal(err.title, err.msg, closeModal, false, "Close");
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (itemData: any) => {
    const cache = await initAllStores();
    setLoading(true);
    if (!type) return;
    try {
      if (isOnline()) {
        await sendRequest("PUT", `/services/${title[type!].toLocaleLowerCase()}/${itemData.item.id}`, itemData.item);
      }

      await cache.updateManyBy(type, [itemData.item.id], {
        ...itemData.item,
        cashPrice: itemData.item.price,
      });

      setItems((prev) =>
        prev.map((it) =>
          it.id === itemData.item.id
            ? {
              ...it,
              ...itemData.item,
              price: itemData.item.price.toLocaleString(),
              status: itemData.item.isAvailable ? "Available" : "Unavailable",
            }
            : it
        )
      );

      showToast("Item Updated Successfully", { type: "success" });
    } catch (error: any) {
      const err = errorMessage(error.status);
      if (err) openModal(err.title, err.msg, closeModal, false, "Close");
    } finally {
      setLoading(false);
    }
  };

  async function deleteItem(id: string, name: string, categoryId: string, categoryName: string) {
    const cache = await initAllStores();
    setLoading(true);
    if (!type) return;
    try {
      if (isOnline()) {
        await sendRequest("DELETE", `/services/${title[type!].toLocaleLowerCase()}?id=${id}`);
      }

      await cache.deleteOne(type, id);
      setItems((prev) => prev.filter((i) => i.id !== id));

      const count = await cache.countBy(type, { [`${categoryKey}Id`]: categoryId });
      if (count < 1) {
        setItemCategories((prevCats) => prevCats.filter((cat) => cat.id !== categoryId));
      }

      openModal(
        "Item Deleted Successfully",
        `"${name}" has been deleted from "${categoryName}".`,
        closeModal,
        false,
        "Close"
      );
    } catch (error: any) {
      console.log(error);
      const err = errorMessage(error.status);
      if (err) openModal(err.title, err.msg, closeModal, false, "Close");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex-1 p-4 md:p-6 space-y-6 overflow-y-auto">
      <InventoryHeader
        title={title[type!] === "Game" ? "Games" : "Laundries"}
        addLabel={`Add New ${title[type!]}`}
        onAdd={() => {
          setAutoFillData(initFormData);
          setIsEdit(false);
          setIsModalOpen(true);
        }}
      />

      <InventoryStats
        total={items.length}
        onManageCategories={() => {
          //navigate(`/inventory/${type}/categories`)
          openModal(
            "Coming Soon 🚀",
            "We’re still working on this feature. Stay tuned for the upcoming release.",
            closeModal, false, "Close"
          )
        }}
      />

      <CategoryFilter
        categories={itemCategories}
        activeCategory={activeCategory}
        onSelect={setActiveCategory}
      />

      {/* Search & Sort */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 items-center">
        <AddItemModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          type={`${title[type!]}` as "Game" | "Laundry"}
          categories={itemCategories.filter((c) => c.id !== "all")}
          onSave={showConfirmationModal}
          isEdit={isEdit}
          autofillData={autoFillData}
        />

        <FullPageSpinner show={loading} />

        <input
          type="text"
          placeholder={`Search ${title[type!].toLowerCase()}s by name...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 w-full sm:w-1/2 focus:ring focus:ring-blue-300"
        />
       {/* <select className="border border-gray-300 rounded-lg px-3 py-2">
          <option>Sort by</option>
          <option>Price</option>
          <option>Status</option>
          <option>Recently Added</option>
        </select>  */}
      </div>

      <Table
        columns={columns}
        data={filteredItems}
        rowsPerPage={PAGINATION_PAGE_NUMBER}
        tableMenu={(row: MappedGameLaundry, closeMenu) => (
          <TableMenu
            row={row}
            closeMenu={closeMenu}
            options={[
              {
                label: `${title[type!]} Details`,
                color: "text-blue-500",
                onClick: (r: any) => {
                  openModal(
                    `${title[type!]} Details`,
                    `-------------------------------------------
                      Name: ${r.name}
                      Category: ${r.category}
                      Price: ₦${r.price}
                      Status: ${r.status === "Available" ? "✅ Available" : "❌ Unavailable"}
                      -------------------------------------------`,
                    closeModal,
                    false,
                    "Close"
                  );
                },
              },
              {
                label: `Update ${title[type!]}`,
                color: "text-blue-500",
                onClick: (data: any) => {
                  setAutoFillData({
                    id: data.id,
                    name: data.name,
                    category: data.category,
                    newCategory: data.categoryId,
                    // stock: undefined,
                    price: data.price.replace(",", ""),
                    status: data.status,
                  });
                  setIsEdit(true);
                  setIsModalOpen(true);
                },
              },
              {
                label: `Delete ${title[type!]}`,
                color: "text-red-500",
                onClick: (r: any) =>
                  openModal(
                    "Confirm Delete",
                    `Are you sure you want to delete "${r.name}"? This action cannot be undone.`,
                    () => {
                      closeModal();
                      deleteItem(r.id, r.name, r.categoryId, r.category);
                    },
                    true,
                    "Delete",
                    "Cancel",
                    "red"
                  ),
              },
            ] as MenuOption<MappedGameLaundry>[]}
          />
        )}
      />
    </div>
  );
};

export default GameLaundryInventory;